/** @format */
import { ApprovalRequiredState, ModuleContentApprovalState, PlanValidationState } from '../../../types/baseTypes.js';
import { CustomModuleAttainment, EntityWithRule } from '../../../types/generated/common-backend.js';
import { PlanValidationResult } from './planValidationResult.js';
import { ValidatablePlan } from './validatablePlan.js';

/**
 * Where a module stands with its module content approval:
 * - NONE: no approval is needed and the student hasn't applied for one
 * - REQUIRED: the module needs an approval and the student hasn't applied for one
 * - REQUESTED, IN_HANDLING, ACCEPTED, CONDITIONAL: the state of the application, which the plan follows
 * - BROKEN_REQUESTED, BROKEN_ACCEPTED: the plan doesn't follow the requested or the accepted (also conditionally) application
 */
export type ModuleContentApprovalPlanState =
    | 'NONE'
    | 'REQUIRED'
    | 'REQUESTED'
    | 'IN_HANDLING'
    | 'ACCEPTED'
    | 'CONDITIONAL'
    | 'BROKEN_REQUESTED'
    | 'BROKEN_ACCEPTED';

export interface ModulePlanState {
    /** ATTAINED when the module is attained, otherwise its validation state */
    validationState: PlanValidationState;
    contentApproval: ModuleContentApprovalPlanState;
}

/**
 * The validation state of a module or a custom module attainment in the plan, and where it stands with its module content approval.
 * Missing without a module, and when the plan has no validation result for a module that isn't attained.
 */
export function getModulePlanState(
    module: EntityWithRule | CustomModuleAttainment | null | undefined,
    validatablePlan: ValidatablePlan,
    planValidationResult: PlanValidationResult | null | undefined,
): ModulePlanState | null {
    if (!module) {
        return null;
    }
    if (module.type === 'CustomModuleAttainment' || validatablePlan.isModuleAttained(module.id)) {
        return { validationState: PlanValidationState.ATTAINED, contentApproval: 'NONE' };
    }
    const result = planValidationResult?.moduleValidationResults?.[module.id];
    if (!result) {
        return null;
    }
    return { validationState: result.state, contentApproval: getContentApproval(module.id, planValidationResult) };
}

function getContentApproval(moduleId: string, planValidationResult: PlanValidationResult): ModuleContentApprovalPlanState {
    const result = planValidationResult.moduleValidationResults[moduleId];
    if (result.approvalRequiredState === ApprovalRequiredState.APPROVAL_REQUIRED) {
        return 'REQUIRED';
    }
    if (result.moduleContentApprovalValidationState === ModuleContentApprovalState.INVALID) {
        return result.isModuleContentApproved ? 'BROKEN_ACCEPTED' : 'BROKEN_REQUESTED';
    }
    if (result.moduleContentApprovalValidationState === ModuleContentApprovalState.VALID) {
        const applicationState = planValidationResult.matchingModuleContentApprovalsByModuleId?.[moduleId]?.state;
        if (isFollowedApplicationState(applicationState)) {
            return applicationState;
        }
    }
    return 'NONE';
}

function isFollowedApplicationState(state: unknown): state is 'REQUESTED' | 'IN_HANDLING' | 'ACCEPTED' | 'CONDITIONAL' {
    return state === 'REQUESTED' || state === 'IN_HANDLING' || state === 'ACCEPTED' || state === 'CONDITIONAL';
}

/** The plan state of a module as one value for display: the key of its text in the SIS_COMPONENTS.PLAN_STATE translations */
export type ModulePlanStateLabel =
    | `${PlanValidationState}`
    | `${ApprovalRequiredState.APPROVAL_REQUIRED}`
    | 'IN_HANDLING'
    | 'MODULECONTENTAPPROVAL_VALIDATION_STATE_INVALID_REQUESTED'
    | 'MODULECONTENTAPPROVAL_VALIDATION_STATE_INVALID_ACCEPTED';

/** The label of a module plan state: the state of the module content approval when there is one, otherwise the validation state */
export function getModulePlanStateLabel({ validationState, contentApproval }: ModulePlanState): ModulePlanStateLabel {
    // When an application matches, the validator sets the module's state to the state of its children (validation.ts), so this is
    // the same as the validationStateOfChildren that the old getPlanStateForModule read
    const partsAttained = validationState === PlanValidationState.PARTS_ATTAINED;
    switch (contentApproval) {
        case 'REQUIRED':
            return ApprovalRequiredState.APPROVAL_REQUIRED;
        case 'REQUESTED':
            return partsAttained ? PlanValidationState.APPROVAL_REQUESTED_PARTS_ATTAINED : PlanValidationState.APPROVAL_REQUESTED;
        case 'IN_HANDLING':
            return 'IN_HANDLING';
        case 'ACCEPTED':
            return partsAttained ? PlanValidationState.APPROVED_PARTS_ATTAINED : PlanValidationState.APPROVED;
        case 'CONDITIONAL':
            return partsAttained ? PlanValidationState.APPROVED_CONDITIONALLY_PARTS_ATTAINED : PlanValidationState.APPROVED_CONDITIONALLY;
        case 'BROKEN_REQUESTED':
            return 'MODULECONTENTAPPROVAL_VALIDATION_STATE_INVALID_REQUESTED';
        case 'BROKEN_ACCEPTED':
            return 'MODULECONTENTAPPROVAL_VALIDATION_STATE_INVALID_ACCEPTED';
        default:
            return validationState;
    }
}
